import {Invoice, PaymentInitResponse} from '../../../api/types/billing';
import {nFetch} from '../../../api/nFetch';
import {ErrorTags, EventNames} from '../../../lib/analytics/events';
import Tracker from '../../../lib/analytics/tracker';

interface RzpHandlerResponse {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
}

interface InvoiceHandlerProps {
    invoice: Invoice;

    // upon change in invoice status
    onNext: (invoice: Invoice) => void;
    onError: (error: Error) => void;

    // upon payment modal close, attempt to cancel invoice
    // defaults to false
    cancelInvoice?: boolean;

    onProcessing: (processing: boolean) => void;
    onLoading: (loading: boolean) => void;
}

// interface InvoicePaymentHandler
// long lived class associated with a single Invoice object, managing lifecycle of Payments
// handles Razorpay payment flow
// two separate workflows handling lifecycle
// [ init -> (user action?) -> processing (rzp modal) -> loop checking status -> paid | cancelled ]
// APIs
// - /get_invoice/:id
// - /pay_invoice/:id
// - /verify_payment/:id
// - /cancel_payment/:id
export class InvoicePaymentHandler {
    static REFUND_MSG = 'If any amount deducted will be refunded.';

    invoice: Invoice;

    __onNext: (invoice: Invoice) => void;
    __onError: (error: Error) => void;

    __onProcessing: (processing: boolean) => void;
    __onLoading: (loading: boolean) => void;

    cancelInvoice: boolean;

    __loading: boolean;
    __timeout: number | undefined;

    constructor(props: InvoiceHandlerProps) {
        this.invoice = props.invoice;
        this.__onNext = props.onNext;
        this.__onError = props.onError;

        this.__onProcessing = props.onProcessing;
        this.__onLoading = props.onLoading;

        this.cancelInvoice = props.cancelInvoice ?? false;

        this.__loading = false;
        this.__timeout = undefined;

        if (this.invoice.status == 'PROCESSING') {
            this._fetchInvoiceStatus(1000);
        }
    }

    _setLoading(loading: boolean) {
        this.__loading = loading;
        this.__onLoading(loading);
    }

    _bail(error: string, retry?: boolean) {
        retry = retry ?? false;
        if (retry) error += ' Please try again later.';
        this.__bail(new Error(`Something went wrong while ${error}`));
    }

    __bail(error: Error) {
        this._setLoading(false);
        this.__onProcessing(false);
        this.__onError(error);
    }

    _success(invoice: Invoice) {
        this.invoice = invoice;
        this.__onNext(invoice);
    }

    __cancel() {
        nFetch<Invoice>(`/billing/cancel_payment/${this.invoice.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                cancel_invoice: this.cancelInvoice,
            }),
        })
            .then((res) => {
                // we enter this with __loading = true
                if (!!res.error) throw res.error;

                this._setLoading(false);
                return res.data!;
            })
            .then((data) => {
                this.invoice = data;
                this._processNextInvoice(data, 0);
            })
            .catch((err) => {
                this._setLoading(false);
                console.error('failed to cancel invoice', err);
                this.__bail(new Error(`Failed to process payment. ${InvoicePaymentHandler.REFUND_MSG}`));
            });
    }

    _cancel() {
        if (this.__timeout) {
            window.clearTimeout(this.__timeout);
            this.__timeout = undefined;
        }
        this._setLoading(true);
        // wait 5000ms for rzp payment status to sync
        setTimeout(() => {
            this.__cancel();
        }, 5000);
    }

    _fetchInvoiceStatus(wait: number) {
        const invoice = this.invoice;
        this._setLoading(true);
        nFetch<Invoice>(`/billing/get_invoice/${invoice.id}`)
            .then((res) => {
                this._setLoading(false);
                if (!!res.error) throw res.error;
                return res.data!;
            })
            .then((data) => {
                this.__timeout = undefined;
                console.debug('fetched invoice status', data);
                this._processNextInvoice(data, Math.min(wait * 2, 32000));
            })
            .catch((err) => {
                console.error('failed to fetch invoice status', err);
                this._bail('fetching invoice status');
            });
    }

    _processNextInvoice(next: Invoice, wait: number) {
        const invoice = this.invoice;
        if (next.status == 'PROCESSING') {
            this.__onProcessing(true);
            if (next.status != invoice.status) {
                this._success(next);
            }
            if (!this.__timeout && wait > 0) {
                this.__timeout = window.setTimeout(() => {
                    this._fetchInvoiceStatus(wait);
                }, wait);
            } else {
                console.info('already polling for invoice status');
            }
        } else if (next.status == 'PAID' || next.status == 'FINAL' || next.status == 'CANCELLED') {
            if (this.__timeout) {
                window.clearTimeout(this.__timeout);
                this.__timeout = undefined;
            }
            this.__onProcessing(false);
            this._success(next);
        } else if (next.status == 'INIT') {
            // unlikely?
            if (this.__timeout) {
                window.clearTimeout(this.__timeout);
                this.__timeout = undefined;
            }
            this.__onProcessing(false);
            this._success(next);
        } else {
            console.error('unknown invoice status', next);
            this._bail('fetching invoice status');
        }
    }

    hasNext() {
        const invoice = this.invoice;
        if (invoice.status == 'INIT') {
            return true;
        } else if (invoice.status == 'PROCESSING') {
            return false;
        } else if (invoice.status == 'PAID' || invoice.status == 'FINAL' || invoice.status == 'CANCELLED') {
            return false;
        }
        return false;
    }

    async next() {
        const invoice = this.invoice;
        const nThis = this;

        Tracker.trackEvent(EventNames.INVOICE_PAY);
        if (invoice.status == 'INIT') {
            this._setLoading(true);
            const result = await nFetch<PaymentInitResponse>(`/billing/pay_invoice/${invoice.id}`, {
                method: 'POST',
            });
            this._setLoading(false);
            if (!!result.error) {
                Tracker.trackError(result.error, ErrorTags.INVOICE_PAY_ERROR);
                console.error('failed to start payment', result.error);
                this._bail('starting payment', true);
                return;
            }

            const data: PaymentInitResponse = result.data!;
            if (data.status === 'PAID') {
                window.location.reload();
                return;
            }
            if (data.status === invoice.status) {
                (data.checkout as any)['handler'] = async function (response: RzpHandlerResponse) {
                    Tracker.trackEvent(EventNames.INVOICE_PAY_RZP_VERIFY);
                    const result = await nFetch<PaymentResponse>(
                        `/billing/verify_payment/${data.invoice.id}`,
                        {
                            method: 'POST',
                            body: JSON.stringify(response),
                        },
                        undefined,
                        false,
                    );
                    if (!!result.error) {
                        Tracker.trackError(result.error, ErrorTags.INVOICE_PAY_RZP_VERIFY_ERROR);
                        console.error('failed to verify payment', result.error);
                        nThis._bail(`verifying payment! ${InvoicePaymentHandler.REFUND_MSG}`);
                        return;
                    }
                    nThis._fetchInvoiceStatus(1000);
                };
                (data.checkout as any)['modal'] = {
                    ondismiss: function () {
                        Tracker.trackEvent(EventNames.INVOICE_PAY_RZP_CANCEL);
                        console.error('payment modal dismissed');
                        nThis._cancel();
                    },
                };
                try {
                    Tracker.trackEvent(EventNames.INVOICE_PAY_RZP);
                    let rzp = new (window as any).Razorpay(data.checkout);
                    rzp.open();
                } catch (e: any) {
                    Tracker.trackError(e, ErrorTags.INVOICE_PAY_RZP_ERROR);
                    console.error('failed to open razorpay', e);
                    this._bail('opening payment gateway', true);
                }
            } else {
                Tracker.trackError(new Error('unexpected invoice status'), ErrorTags.INVOICE_PAY_ERROR);
                console.error('unexpected invoice status', data);
                this._bail('starting payment', true);
            }
        } else if (invoice.status == 'PROCESSING') {
            // ideally we cannot press this button when invoice is processing
        } else if (invoice.status == 'PAID' || invoice.status == 'FINAL' || invoice.status == 'CANCELLED') {
            // ideally we cannot press this button when invoice is paid/final/cancelled
        }
    }
}
